import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Badge, Tooltip, Typography } from 'antd';
import {
  MessageOutlined,
  CloseOutlined,
  SendOutlined,
  RobotOutlined,
  UserOutlined,
  QuestionCircleOutlined,
  ExpandOutlined,
  CompressOutlined,
} from '@ant-design/icons';
import { chatbotAPI } from '../services/api';
import { brand, gray, color, radius, shadow, font, space } from '../theme';

interface ChatMessage {
  id: number;
  role: 'user' | 'bot';
  content: string;
  time: string;
}

const QUICK_ACTIONS = [
  { label: '현황', value: '현황' },
  { label: '일정 현황', value: '일정 현황' },
  { label: '계획서 현황', value: '계획서 현황' },
  { label: '의뢰사', value: '의뢰사' },
  { label: '최근 활동', value: '최근 활동' },
  { label: '오늘 일정', value: '오늘 일정' },
  { label: '인기 검색어', value: '인기 검색어' },
  { label: '메뉴 안내', value: '메뉴 안내' },
];

const ChatBot: React.FC = () => {
  const [open, setOpen] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [messages, setMessages] = useState<ChatMessage[]>([
    {
      id: 0,
      role: 'bot',
      content: '안녕하세요! SAPPT 도우미입니다.\n무엇을 도와드릴까요? 아래 빠른 명령어를 클릭하거나 직접 입력해주세요.',
      time: new Date().toLocaleTimeString('ko-KR', { hour: '2-digit', minute: '2-digit', hour12: false }),
    },
  ]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [hasNew, setHasNew] = useState(false);
  const [showGuide, setShowGuide] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  let msgId = useRef(1);

  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [messages, scrollToBottom]);

  useEffect(() => {
    if (open) {
      setHasNew(false);
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [open]);

  const sendMessage = async (text: string) => {
    if (!text.trim() || loading) return;

    const now = new Date().toLocaleTimeString('ko-KR', { hour: '2-digit', minute: '2-digit', hour12: false });
    const userMsg: ChatMessage = { id: msgId.current++, role: 'user', content: text.trim(), time: now };
    setMessages((prev) => [...prev, userMsg]);
    setInput('');
    setLoading(true);

    // 스트리밍 응답용 bot 메시지 미리 추가
    const botId = msgId.current++;
    const botTime = new Date().toLocaleTimeString('ko-KR', { hour: '2-digit', minute: '2-digit', hour12: false });
    setMessages((prev) => [...prev, { id: botId, role: 'bot', content: '', time: botTime }]);
    let accumulated = '';

    try {
      await chatbotAPI.stream(text.trim(), {
        onChunk: (t) => {
          accumulated += t;
          setMessages((prev) => prev.map((m) => (m.id === botId ? { ...m, content: accumulated } : m)));
        },
        onReply: (t) => {
          accumulated = t;
          setMessages((prev) => prev.map((m) => (m.id === botId ? { ...m, content: accumulated } : m)));
        },
        onError: () => {
          setMessages((prev) => prev.map((m) => (m.id === botId ? { ...m, content: '죄송합니다. 요청 처리 중 오류가 발생했습니다.' } : m)));
        },
      });
      if (!open) setHasNew(true);
    } finally {
      setLoading(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage(input);
    }
  };

  return (
    <>
      {/* Floating Button */}
      <div
        onClick={() => setOpen(!open)}
        style={{
          position: 'fixed', bottom: space.lg, right: space.lg, zIndex: 1000,
          width: 56, height: 56, borderRadius: radius.full,
          background: brand.primaryGradient,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          cursor: 'pointer', boxShadow: shadow.brand,
          transition: 'all 0.3s ease',
        }}
        onMouseEnter={(e) => { e.currentTarget.style.transform = 'scale(1.1)'; }}
        onMouseLeave={(e) => { e.currentTarget.style.transform = 'scale(1)'; }}
      >
        <Badge dot={hasNew} offset={[-2, 2]}>
          {open ? (
            <CloseOutlined style={{ fontSize: 22, color: '#fff' }} />
          ) : (
            <MessageOutlined style={{ fontSize: 22, color: '#fff' }} />
          )}
        </Badge>
      </div>

      {/* Chat Window */}
      {open && (
        <div style={{
          position: 'fixed', bottom: 92, right: space.lg, zIndex: 999,
          width: expanded ? 'min(760px, calc(100vw - 48px))' : 'min(440px, calc(100vw - 48px))',
          height: expanded ? 'calc(100vh - 140px)' : 'min(620px, calc(100vh - 140px))',
          borderRadius: radius.xl,
          background: color.bg, boxShadow: shadow.xl,
          display: 'flex', flexDirection: 'column', overflow: 'hidden',
          border: `1px solid ${color.border}`,
          animation: 'chatSlideUp 0.3s ease',
        }}>
          {/* Header */}
          <div style={{
            background: brand.primaryGradient,
            padding: `${space.base}px ${space.lg - 4}px`, display: 'flex', alignItems: 'center', gap: space.md,
          }}>
            <div style={{
              width: 36, height: 36, borderRadius: '50%',
              background: 'rgba(255,255,255,0.2)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              <RobotOutlined style={{ fontSize: 20, color: '#fff' }} />
            </div>
            <div style={{ flex: 1 }}>
              <div style={{ color: color.textOnPrimary, fontWeight: font.weight.bold, fontSize: font.size.md }}>SAPPT 도우미</div>
              <div style={{ color: 'rgba(255,255,255,0.7)', fontSize: font.size.xs }}>시스템 현황 조회 및 검색</div>
            </div>
            <Tooltip title={expanded ? '축소' : '확대'}>
              {expanded ? (
                <CompressOutlined
                  style={{ color: 'rgba(255,255,255,0.85)', fontSize: 16, cursor: 'pointer', marginRight: 4 }}
                  onClick={() => setExpanded(false)}
                />
              ) : (
                <ExpandOutlined
                  style={{ color: 'rgba(255,255,255,0.85)', fontSize: 16, cursor: 'pointer', marginRight: 4 }}
                  onClick={() => setExpanded(true)}
                />
              )}
            </Tooltip>
            <Tooltip title="도움말">
              <QuestionCircleOutlined
                style={{ color: 'rgba(255,255,255,0.7)', fontSize: 16, cursor: 'pointer' }}
                onClick={() => sendMessage('도움말')}
              />
            </Tooltip>
          </div>

          {/* Messages */}
          <div style={{
            flex: 1, overflowY: 'auto', padding: space.base,
            background: gray[50],
          }}>
            {messages.map((msg) => (
              <div
                key={msg.id}
                style={{
                  display: 'flex', flexDirection: msg.role === 'user' ? 'row-reverse' : 'row',
                  marginBottom: 12, gap: 8, alignItems: 'flex-start',
                }}
              >
                {/* Avatar */}
                <div style={{
                  width: 30, height: 30, borderRadius: '50%', flexShrink: 0,
                  background: msg.role === 'bot'
                    ? 'linear-gradient(135deg, #4f46e5, #818cf8)'
                    : 'linear-gradient(135deg, #64748b, #94a3b8)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                  {msg.role === 'bot'
                    ? <RobotOutlined style={{ color: '#fff', fontSize: 14 }} />
                    : <UserOutlined style={{ color: '#fff', fontSize: 14 }} />}
                </div>

                {/* Bubble */}
                <div style={{
                  maxWidth: msg.role === 'bot' ? '92%' : '78%',
                  padding: `${space.md - 2}px ${space.md + 2}px`, borderRadius: radius.lg,
                  background: msg.role === 'user' ? brand.primary : color.bg,
                  color: msg.role === 'user' ? color.textOnPrimary : color.text,
                  fontSize: font.size.base, lineHeight: font.lineHeight.base, whiteSpace: 'pre-wrap',
                  fontFamily: msg.role === 'bot' ? font.mono : 'inherit',
                  wordBreak: 'break-word',
                  boxShadow: msg.role === 'bot' ? shadow.md : 'none',
                  borderTopLeftRadius: msg.role === 'bot' ? 4 : radius.lg,
                  borderTopRightRadius: msg.role === 'user' ? 4 : radius.lg,
                }}>
                  {msg.content}
                  <div style={{
                    fontSize: 10, marginTop: 4, textAlign: 'right',
                    color: msg.role === 'user' ? 'rgba(255,255,255,0.6)' : '#94a3b8',
                  }}>
                    {msg.time}
                  </div>
                </div>
              </div>
            ))}

            {loading && (
              <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 12 }}>
                <div style={{
                  width: 30, height: 30, borderRadius: '50%', flexShrink: 0,
                  background: 'linear-gradient(135deg, #4f46e5, #818cf8)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                  <RobotOutlined style={{ color: '#fff', fontSize: 14 }} />
                </div>
                <div style={{
                  padding: '10px 14px', borderRadius: 12, borderTopLeftRadius: 4,
                  background: '#fff', boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
                }}>
                  <div style={{ display: 'flex', gap: 4 }}>
                    <span className="chat-dot" style={{ animationDelay: '0s' }} />
                    <span className="chat-dot" style={{ animationDelay: '0.2s' }} />
                    <span className="chat-dot" style={{ animationDelay: '0.4s' }} />
                  </div>
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* Quick Actions */}
          <div style={{
            padding: '8px 16px', background: '#fff',
            borderTop: '1px solid #f1f5f9',
            display: 'flex', gap: 6, flexWrap: 'wrap',
          }}>
            {QUICK_ACTIONS.map((qa) => (
              <button
                key={qa.value}
                onClick={() => sendMessage(qa.value)}
                disabled={loading}
                style={{
                  padding: '4px 10px', borderRadius: 12,
                  border: '1px solid #e2e8f0', background: '#f8fafc',
                  fontSize: 11, color: '#475569', cursor: 'pointer',
                  transition: 'all 0.2s',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = '#eef2ff';
                  e.currentTarget.style.borderColor = '#c7d2fe';
                  e.currentTarget.style.color = '#4f46e5';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = '#f8fafc';
                  e.currentTarget.style.borderColor = '#e2e8f0';
                  e.currentTarget.style.color = '#475569';
                }}
              >
                {qa.label}
              </button>
            ))}
          </div>

          {/* 사용 예시 가이드 (토글) */}
          {showGuide && (
            <div id="usage-examples-panel" style={{
              padding: '14px 16px', borderTop: '1px solid #e5e7eb',
              background: 'linear-gradient(135deg, #eef2ff 0%, #f0f9ff 100%)', fontSize: 12, color: '#1e293b',
              maxHeight: 340, overflowY: 'auto',
            }}>
              <div style={{ fontWeight: 700, marginBottom: 10, fontSize: 13, color: '#4338ca', display: 'flex', alignItems: 'center', gap: 6 }}>
                {'💡'} 가능 질문 & 예시 답변
              </div>

              {[
                { q: '현황', a: '📊 시험계획서 N건, 시험일정 N건, 의뢰사 N개, 시험장소 N개 등 전체 시스템 요약' },
                { q: '일정 현황', a: '📋 시험일정 총 건수 + 상태별(진행중/완료/예정) · 연도별 집계' },
                { q: '계획서 현황', a: '📝 시험계획서 총 건수 + 기관별 등록 현황' },
                { q: '일정 검색 수박', a: '🔍 "수박" 관련 시험일정 검색\n  예) [진행중] SR2401-001 - 수박 약효시험 (○○농약)' },
                { q: '계획서 검색 벼', a: '🔍 "벼" 관련 계획서 검색\n  예) P2401-003 - 벼 제초제 효과시험 (벼)' },
                { q: '검색 SR22', a: '🔍 시험번호/키워드로 일정+계획서 통합 검색' },
                { q: '의뢰사', a: '🏢 전체 의뢰사 목록 + 각 의뢰사별 시험 건수 표시' },
                { q: '오늘 일정', a: '📅 현재 진행 중인 시험일정 목록 표시' },
                { q: '최근 활동', a: '📜 최근 활동이력 (로그인/생성/수정/삭제 등)' },
                { q: '인기 검색어', a: '🔥 최근 7일간 인기 검색 키워드 순위' },
                { q: '메뉴 안내', a: '시스템 전체 메뉴 구조와 각 기능 설명 안내' },
                { q: '진행중인 시험은 몇 건이야?', a: '🤖 AI가 자연어를 이해하여 데이터 기반 답변 제공' },
              ].map((item, idx) => (
                <div key={idx} style={{
                  marginBottom: 8, padding: '8px 12px', borderRadius: 8,
                  background: '#fff', border: '1px solid #e0e7ff',
                  cursor: 'pointer', transition: 'all 0.2s',
                }}
                  onClick={() => { setShowGuide(false); sendMessage(item.q); }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.borderColor = '#818cf8';
                    e.currentTarget.style.background = '#f5f3ff';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.borderColor = '#e0e7ff';
                    e.currentTarget.style.background = '#fff';
                  }}
                >
                  <div style={{ fontWeight: 600, color: '#4f46e5', fontSize: 12, marginBottom: 3 }}>
                    Q. {item.q}
                  </div>
                  <div style={{ color: '#475569', fontSize: 11, lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>
                    {item.a}
                  </div>
                </div>
              ))}

              <div style={{ marginTop: 8, padding: '6px 10px', borderRadius: 6, background: 'rgba(99,102,241,0.08)', fontSize: 11, color: '#6366f1', lineHeight: 1.5 }}>
                <b>Tip:</b> 위 질문을 클릭하면 바로 실행됩니다. 자연어 질문도 자유롭게 입력해보세요!
              </div>
            </div>
          )}

          {/* Input */}
          <div style={{
            padding: '12px 16px', borderTop: '1px solid #e5e7eb',
            display: 'flex', gap: 8, background: '#fff',
          }}>
            <Tooltip title={showGuide ? '사용 예시 접기' : '사용 예시 보기'}>
              <button
                onClick={() => setShowGuide((v) => !v)}
                style={{
                  width: 40, height: 40, borderRadius: 10,
                  border: '1px solid #e2e8f0', background: showGuide ? '#eef2ff' : '#f8fafc',
                  color: showGuide ? '#4f46e5' : '#64748b',
                  cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
                  transition: 'all 0.2s',
                }}
              >
                <QuestionCircleOutlined style={{ fontSize: 16 }} />
              </button>
            </Tooltip>
            <input
              ref={inputRef}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="메시지를 입력하세요..."
              disabled={loading}
              style={{
                flex: 1, padding: '10px 14px', borderRadius: 10,
                border: '1px solid #e2e8f0', outline: 'none',
                fontSize: 13, background: '#f8fafc',
                transition: 'border-color 0.2s',
              }}
              onFocus={(e) => { e.target.style.borderColor = '#a5b4fc'; }}
              onBlur={(e) => { e.target.style.borderColor = '#e2e8f0'; }}
            />
            <button
              onClick={() => sendMessage(input)}
              disabled={loading || !input.trim()}
              style={{
                width: 40, height: 40, borderRadius: 10,
                border: 'none',
                background: input.trim() ? 'linear-gradient(135deg, #4f46e5, #6366f1)' : '#e2e8f0',
                color: input.trim() ? '#fff' : '#94a3b8',
                cursor: input.trim() ? 'pointer' : 'default',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                transition: 'all 0.2s',
              }}
            >
              <SendOutlined style={{ fontSize: 16 }} />
            </button>
          </div>
        </div>
      )}

      {/* CSS Animation */}
      <style>{`
        @keyframes chatSlideUp {
          from { opacity: 0; transform: translateY(20px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes chatDotPulse {
          0%, 80%, 100% { opacity: 0.3; transform: scale(0.8); }
          40% { opacity: 1; transform: scale(1); }
        }
        .chat-dot {
          display: inline-block;
          width: 7px; height: 7px; border-radius: 50%;
          background: #94a3b8;
          animation: chatDotPulse 1.4s ease-in-out infinite;
        }
      `}</style>
    </>
  );
};

export default ChatBot;
